/**
 * Brief vtw-ih-02/2026-10-30-006: recover when a page chunk of an older build is gone.
 *
 * Every page is `lazy()` (V17.523). A tab still running the previous build asks for that
 * build's chunk after a deploy; it is gone, and CloudFront answers a missing file with
 * index.html, so the dynamic import fails. One reload fetches the new index.html and its
 * chunks. A timestamp in sessionStorage keeps it to one reload per 30 s, so a chunk that
 * stays broken shows a message (ChunkErrorBoundary) instead of a reload loop.
 */

export const CHUNK_RELOAD_KEY = 'vtw_chunk_reload_at';
export const CHUNK_RELOAD_WINDOW_MS = 30_000;

// Chrome / Edge, Safari, Firefox, the strict MIME check, webpack-style and Vite CSS preload.
const CHUNK_ERROR = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Expected a JavaScript(-or-Wasm)? module script/i,
  /'text\/html' is not a valid JavaScript MIME type/i,
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /Unable to preload CSS/i,
];

/** True only for a failed chunk load — other errors are never turned into a reload. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === 'ChunkLoadError') return true;
  return typeof message === 'string' && CHUNK_ERROR.some((re) => re.test(message));
}

// Set once a reload is under way in this page, so a second error from the same failure
// (the preload event and the import both fire) waits for it instead of showing a message.
let reloadPending = false;

/**
 * Reload once for a chunk error. Returns true when a reload is under way (started now or
 * earlier in this page), false when the guard says "already reloaded in the last 30 s" —
 * the caller then shows the message. No sessionStorage = no guard = no automatic reload.
 */
export function reloadOnceForChunkError(
  now: number = Date.now(),
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null,
  reload: () => void = () => window.location.reload(),
): boolean {
  if (reloadPending) return true;
  try {
    // Reading sessionStorage itself can throw (blocked storage), so it is inside the try.
    const store = storage === undefined ? globalThis.sessionStorage : storage;
    if (!store) return false;
    const last = Number(store.getItem(CHUNK_RELOAD_KEY));
    if (last && now - last >= 0 && now - last < CHUNK_RELOAD_WINDOW_MS) return false;
    store.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  reloadPending = true;
  reload();
  return true;
}

/** A reload is under way in this page (the page is about to go away). */
export function isChunkReloadPending(): boolean {
  return reloadPending;
}

/**
 * Vite fires `vite:preloadError` when a chunk's preload fails; reload instead of throwing.
 * Once prevented, Vite resolves the import with `undefined`, so `lazy()` then throws a plain
 * TypeError — ChunkErrorBoundary waits for the reload on any error while one is pending.
 */
export function installPreloadErrorReload(target: Window = window): void {
  target.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceForChunkError()) event.preventDefault();
  });
}

/** Tests only. */
export function resetChunkReloadForTests(): void {
  reloadPending = false;
}
